import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isSignInWithEmailLink } from 'firebase/auth';
import { auth } from './firebase';
import { useAuthUser } from './authState';
import { tenantFromLocation } from './tenant';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Layout from './components/Layout';
import DashboardHome from './components/DashboardHome';
import TalentBank from './components/TalentBank';
import MyTalentBank from './components/MyTalentBank';
import StaffMgt from './components/StaffMgt';
import StudentMgt from './components/StudentMgt';
import AccessControl from './components/AccessControl';
import MDMUser from './components/MDMUser';
import MDMAdmin from './components/MDMAdmin';
import FacilityBookingUser from './components/FacilityBookingUser';
import CampusManagement from './components/CampusManagement';
import InventoryUser from './components/InventoryUser';
import InventoryAdmin from './components/InventoryAdmin';
import TimetableAdmin from './components/TimetableAdmin';
import LessonAllocationAdmin from './components/LessonAllocationAdmin';
import SubstitutionAdmin from './components/SubstitutionAdmin';
import CalendarAdmin from './components/CalendarAdmin';
import CalendarUser from './components/CalendarUser';
import SystemSettings from './components/SystemSettings';
import MigrateDB from './components/MigrateDB';
import DeveloperDashboard from './components/DeveloperDashboard';
import BudgetControl from './components/BudgetControl';
import Assessment from './components/Assessment';
import AdminDuty from './components/AdminDuty';
import TeachingDuty from './components/TeachingDuty';
import MaintenanceReport from './components/MaintenanceReport';
import OLEManagement from './components/OLEManagement';
import StudentCredit from './components/StudentCredit';
import MyTimetable from './components/MyTimetable';
import MyDuties from './components/MyDuties';
import ExamScope from './components/ExamScope';
import FormsAndApplication from './components/FormsAndApplication';
import Attendance from './components/Attendance';
import MyMessage from './components/MyMessage';
import MyMessagesUser from './components/MyMessagesUser';
import MyAttendanceUser from './components/MyAttendanceUser';
import ParentNotice from './components/ParentNotice';
import MyNotice from './components/MyNotice';
import SalaryAudit from './components/SalaryAudit';
import Invigilation from './components/Invigilation';
import SENInvigilation from './components/SENInvigilation';
import SeatingPlan from './components/SeatingPlan';
import PatrolDuty from './components/PatrolDuty';
import QuizTutorial from './components/QuizTutorial';

// What "/" shows.
//
// This used to render <Login /> whenever the URL carried a tenant, without ever asking whether
// somebody was already signed in. In a browser tab that went unnoticed -- people arrive at
// /dashboard/... from a bookmark or history. An installed app has no history: its start_url is "/",
// so every cold start landed here and asked a signed-in user to sign in again. The session had been
// there the whole time.
const RootRoute = () => {
  const user = useAuthUser();
  const tenant = tenantFromLocation();

  // An email sign-in link has to reach Login even for a session that already exists, or clicking a
  // fresh link while signed in would silently do nothing.
  const finishingEmailLink = isSignInWithEmailLink(auth, window.location.href);

  // Deliberately blank rather than a spinner or the login form. This resolves in a few milliseconds
  // from local storage; a spinner would flash, and the login form would be a lie.
  if (user === undefined && !finishingEmailLink) return null;

  if (user && !finishingEmailLink) return <Navigate to="/dashboard" replace />;

  // No tenant in the address means the bare product domain, which is the product's own page and
  // names no school. It used to list every school here; see LandingPage for why that had to go.
  return tenant ? <Login /> : <LandingPage />;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/login" element={<Login />} />
        <Route path="/migrate" element={<MigrateDB />} />
        <Route path="/developer" element={<DeveloperDashboard />} />
        
        {/* Dashboard Nested Routes */}
        <Route path="/dashboard" element={<Layout />}>
          <Route index element={<DashboardHome />} />
          <Route path="my-message" element={<MyMessage />} />
          <Route path="my-messages" element={<MyMessagesUser />} />
          <Route path="my-attendance" element={<MyAttendanceUser />} />
          <Route path="talent-bank" element={<TalentBank />} />
          <Route path="my-talent-bank" element={<MyTalentBank />} />
          <Route path="social-credit" element={<StudentCredit />} />
          <Route path="parent-notice" element={<ParentNotice />} />
          <Route path="my-notice" element={<MyNotice />} />
          <Route path="attendance" element={<Attendance />} />
          <Route path="salary-audit" element={<SalaryAudit />} />
          <Route path="my-timetable" element={<MyTimetable />} />
          <Route path="my-duties" element={<MyDuties />} />
          <Route path="exam-scope" element={<ExamScope />} />
          
          {/* MDM Modules */}
          <Route path="mdm-user" element={<MDMUser />} />
          <Route path="mdm-admin" element={<MDMAdmin />} />

          {/* Campus Management */}
          <Route path="facility-booking" element={<FacilityBookingUser />} />
          <Route path="campus-management" element={<CampusManagement />} />

          {/* Inventory Management */}
          <Route path="inventory-user" element={<InventoryUser />} />
          <Route path="inventory-admin" element={<InventoryAdmin />} />

          {/* School Calendar */}
          <Route path="my-timetable" element={<CalendarUser />} />
          <Route path="timetable-profiles" element={<TimetableAdmin />} />
          <Route path="calendar-admin" element={<CalendarAdmin />} />

          {/* Under Construction Modules */}
          <Route path="budget-control" element={<BudgetControl />} />
          <Route path="ole-management" element={<OLEManagement />} />
          <Route path="quiz-tutorial" element={<QuizTutorial />} />
          <Route path="assessment" element={<Assessment />} />
          <Route path="maintenance-report" element={<MaintenanceReport />} />
          <Route path="forms-and-application" element={<FormsAndApplication />} />
          <Route path="invigilation" element={<Invigilation />} />
          <Route path="sen-invigilation" element={<SENInvigilation />} />
          <Route path="seating-plan" element={<SeatingPlan />} />
          <Route path="patrol-duty" element={<PatrolDuty />} />
          <Route path="manpower-allocation" element={<AdminDuty />} />
          <Route path="teaching-duty" element={<TeachingDuty />} />

          <Route path="student-mgt" element={<StudentMgt />} />
          <Route path="staff-mgt" element={<StaffMgt />} />
          <Route path="access-control" element={<AccessControl />} />
          <Route path="teacher-timetable" element={<LessonAllocationAdmin />} />
          <Route path="substitution" element={<SubstitutionAdmin />} />
          <Route path="system-settings" element={<SystemSettings />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
